// Weather forecast via Open-Meteo (free, no key, CORS-enabled).
// Forecast horizon is ~16 days; outside that we report "unavailable".

import { haversineKm } from './geomath';

const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const ROUTE_WEATHER_FN = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getRouteWeather';
export const FORECAST_MAX_DAYS = 15;

// WMO weather code → emoji + short label.
export function weatherInfo(code) {
  if (code === 0)                       return { icon: '☀️', label: 'Clear' };
  if (code === 1 || code === 2)         return { icon: '⛅', label: 'Partly cloudy' };
  if (code === 3)                       return { icon: '☁️', label: 'Overcast' };
  if (code === 45 || code === 48)       return { icon: '🌫️', label: 'Fog' };
  if (code >= 51 && code <= 57)         return { icon: '🌦️', label: 'Drizzle' };
  if (code >= 61 && code <= 67)         return { icon: '🌧️', label: 'Rain' };
  if (code >= 71 && code <= 77)         return { icon: '❄️', label: 'Snow' };
  if (code >= 80 && code <= 82)         return { icon: '🌧️', label: 'Showers' };
  if (code === 85 || code === 86)       return { icon: '🌨️', label: 'Snow showers' };
  if (code >= 95)                       return { icon: '⛈️', label: 'Thunderstorm' };
  return { icon: '🌡️', label: 'Forecast' };
}

// Whole days from today to the given date (local). Negative = past.
export function daysUntil(date) {
  const d = date.toDate ? date.toDate() : new Date(date);
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
}

function toDateStr(date) {
  const d = date.toDate ? date.toDate() : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * @returns {Promise<null | { code, tMax, tMin, precip, wind }>}
 *          null when the request fails (callers handle range separately).
 */
export async function fetchTourWeather(lat, lng, date, signal) {
  const dateStr = toDateStr(date);
  const params = new URLSearchParams({
    latitude: lat,
    longitude: lng,
    daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max,sunrise,sunset',
    timezone: 'auto',
    start_date: dateStr,
    end_date: dateStr,
  });
  try {
    const res = await fetch(`${FORECAST_URL}?${params}`, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const d = data?.daily;
    if (!d || !Array.isArray(d.time) || !d.time.length) return null;
    return {
      code:    d.weather_code?.[0],
      tMax:    Math.round(d.temperature_2m_max?.[0]),
      tMin:    Math.round(d.temperature_2m_min?.[0]),
      precip:  d.precipitation_probability_max?.[0] ?? null,
      wind:    Math.round(d.wind_speed_10m_max?.[0]),
      sunrise: fmtClock(d.sunrise?.[0]),
      sunset:  fmtClock(d.sunset?.[0]),
    };
  } catch {
    return null;
  }
}

// ── Weather along a route ──────────────────────────────────────────────────

/**
 * Pick N points evenly by distance along a route, with the cumulative travel
 * time (minutes) to reach each — used to look up the forecast at the hour the
 * rider will actually be there.
 * @param {number[][]} coords GeoJSON [lng,lat] pairs
 * @param {number} totalDurationSec route duration
 * @param {number} n number of sample points
 */
export function sampleRoutePoints(coords, totalDurationSec, n = 7) {
  if (!coords || coords.length < 2) return [];
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    cum[i] = cum[i - 1] + haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]);
  }
  const total = cum[cum.length - 1];
  if (total <= 0) return [];

  const pts = [];
  for (let k = 0; k < n; k++) {
    const f = n === 1 ? 0 : k / (n - 1);
    const targetD = f * total;
    let i = 1;
    while (i < cum.length - 1 && cum[i] < targetD) i++;
    const [lng, lat] = coords[i];
    pts.push({
      lat, lng,
      fraction: f,
      km: targetD,
      etaMin: Math.round(f * ((totalDurationSec || 0) / 60)),
    });
  }
  return pts;
}

/**
 * Fetch the hourly forecast for the sampled points (via our cached Cloud
 * Function). Returns the raw per-point `hourly` blocks; arrival-hour selection
 * happens locally so changing the departure time needs no new request.
 */
export async function fetchRouteWeather(samples, days, signal) {
  if (!samples?.length) return null;
  const lats = samples.map(s => Number(s.lat.toFixed(4)));
  const lngs = samples.map(s => Number(s.lng.toFixed(4)));
  try {
    const res = await fetch(ROUTE_WEATHER_FN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lats, lngs, days: Math.min(7, Math.max(1, days || 2)) }),
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json?.data) ? json.data : null;
  } catch {
    return null;
  }
}

// Select the forecast values at a point's arrival time from its hourly block.
// Times from the API are GMT-naive ISO strings ("2026-06-21T14:00").
export function pickArrival(hourly, arrivalMs) {
  if (!hourly?.time?.length) return null;
  let bi = 0, bd = Infinity;
  for (let j = 0; j < hourly.time.length; j++) {
    const t = Date.parse(hourly.time[j] + 'Z');
    const d = Math.abs(t - arrivalMs);
    if (d < bd) { bd = d; bi = j; }
  }
  const num = (arr) => (arr && arr[bi] != null ? Math.round(arr[bi]) : null);
  return {
    code:   hourly.weather_code?.[bi],
    temp:   num(hourly.temperature_2m),
    feels:  num(hourly.apparent_temperature),
    precip: hourly.precipitation_probability?.[bi] ?? null,
    wind:   num(hourly.wind_speed_10m),
    gust:   num(hourly.wind_gusts_10m),
    beyond: bd > 3 * 3600000,   // arrival outside the fetched forecast window
  };
}

// "2026-06-20T05:12" → "5:12 am"
function fmtClock(iso) {
  if (!iso) return null;
  const t = iso.split('T')[1];
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
